import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Popup from '../common/Popup';
import { paths } from '../../config/paths';

/**
 * 3.0 user `offline.js` fnOpenLocationMapPopup 과 동일 좌표·문구.
 * mapTp: 1 본사(방배) / 2 별관(신사)
 */
const MAP_LOCATIONS = {
  1: {
    lat: 37.4785034739701,
    lng: 127.006171341345,
    title: '마크로밀엠브레인 본사(방배동)',
    addressLine: '06714 서울시 서초구 명달로 9 방배빌딩 1, 7~9층',
  },
  2: {
    lat: 37.5214396,
    lng: 127.03097,
    title: '마크로밀엠브레인 별관(신사동)',
    addressLine: (
      <>
        서울특별시 강남구 논현로152길 34 선인빌딩 5층 <span className="iblock">(압구정역 3번 출구)</span>
      </>
    ),
  },
};

function getNcpKeyId() {
  return (
    process.env.REACT_APP_NAVER_MAP_NCP_KEY_ID ||
    process.env.REACT_APP_NAVER_MAP_CLIENT_ID ||
    ''
  ).trim();
}

function loadNaverMapsScript(ncpKeyId) {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.naver && window.naver.maps) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const prev = document.querySelector('script[data-survey-offline-naver-maps="1"]');
    if (prev) {
      const done = () => {
        if (window.naver && window.naver.maps) resolve();
        else reject(new Error('naver maps unavailable'));
      };
      if (window.naver && window.naver.maps) {
        resolve();
        return;
      }
      prev.addEventListener('load', done);
      prev.addEventListener('error', () => reject(new Error('naver maps script error')));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${encodeURIComponent(ncpKeyId)}`;
    s.async = true;
    s.dataset.surveyOfflineNaverMaps = '1';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('naver maps script load failed'));
    document.head.appendChild(s);
  });
}

function osmEmbedSrc(lat, lng) {
  const d = 0.004;
  const minLon = lng - d;
  const minLat = lat - d * 0.9;
  const maxLon = lng + d;
  const maxLat = lat + d * 0.9;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
    `${minLon},${minLat},${maxLon},${maxLat}`,
  )}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lng}`)}`;
}

/** 세션 내 네이버 실패(인증·로드) 후 재오픈 시 OSM 유지 — 회색 빈 맵 방지 */
let naverMapsPreferOsm = false;

function shouldPreferOsm() {
  return naverMapsPreferOsm || !getNcpKeyId();
}

/**
 * 좌담회 장소 지도 팝업 (3.0 Naver Map + 안내 문구)
 * @param {{ open: boolean, onClose: () => void, mapTp: number }} props
 */
export default function SurveyOfflineLocationMapPopup({ open, onClose, mapTp }) {
  const tp = mapTp === 2 ? 2 : 1;
  const loc = MAP_LOCATIONS[tp];
  const mapElRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const infoRef = useRef(null);
  const [useOsm, setUseOsm] = useState(() => shouldPreferOsm());

  const cleanupNaver = useCallback(() => {
    try {
      if (infoRef.current) {
        infoRef.current.close();
        infoRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapObjRef.current) {
        mapObjRef.current.destroy?.();
        mapObjRef.current = null;
      }
    } catch {
      /* no-op */
    }
    if (mapElRef.current) {
      mapElRef.current.innerHTML = '';
    }
  }, []);

  /** 네이버 로드·인증·초기화 실패 시 OpenStreetMap 으로 전환 */
  const fallbackToOsm = useCallback(() => {
    naverMapsPreferOsm = true;
    cleanupNaver();
    setUseOsm(true);
  }, [cleanupNaver]);

  useEffect(() => {
    if (!open) {
      cleanupNaver();
      // 닫을 때 useOsm 을 키 유무로 리셋하지 않음 — 폴백 후 재오픈 시 네이버 빈 회색 영역 방지
      return;
    }

    if (shouldPreferOsm()) {
      setUseOsm(true);
      return;
    }

    if (useOsm) {
      return;
    }

    const key = getNcpKeyId();
    if (!key) {
      fallbackToOsm();
      return;
    }

    let cancelled = false;
    const prevAuthFailure = window.navermap_authFailure;
    /** 공식 인증 실패 훅 — 500/인증 오류 시 OSM 폴백 (스크립트 onload 이후에도 호출됨) */
    window.navermap_authFailure = () => {
      if (!cancelled) {
        fallbackToOsm();
      }
      if (typeof prevAuthFailure === 'function') {
        prevAuthFailure();
      }
    };

    const run = async () => {
      try {
        await loadNaverMapsScript(key);
        if (cancelled || !mapElRef.current || !window.naver?.maps) {
          if (!cancelled) fallbackToOsm();
          return;
        }

        const center = new window.naver.maps.LatLng(loc.lat, loc.lng);
        const map = new window.naver.maps.Map(mapElRef.current, {
          center,
          zoom: 17,
          zoomControl: true,
          zoomControlOptions: { position: window.naver.maps.Position.LEFT_BOTTOM },
        });
        mapObjRef.current = map;

        const marker = new window.naver.maps.Marker({
          position: center,
          map,
        });
        markerRef.current = marker;

        const iw = new window.naver.maps.InfoWindow({
          content: `<div style="padding:2px 4px;"><b>${loc.title}</b></div>`,
        });
        iw.open(map, marker);
        infoRef.current = iw;

        window.naver.maps.Event.trigger(map, 'resize');
      } catch {
        if (!cancelled) fallbackToOsm();
      }
    };

    const timer = window.setTimeout(run, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (window.navermap_authFailure) {
        window.navermap_authFailure = prevAuthFailure;
      }
      cleanupNaver();
    };
  }, [open, tp, loc.lat, loc.lng, loc.title, useOsm, cleanupNaver, fallbackToOsm]);

  return (
    <Popup open={open} onClose={onClose} title="좌담회 장소" className="panel-location-popup">
      <>
        <div className="map-container">
          {useOsm ? (
            <iframe
              title="좌담회 장소 지도"
              src={osmEmbedSrc(loc.lat, loc.lng)}
              style={{ width: '100%', height: 350, border: 0, display: 'block' }}
              loading="lazy"
            />
          ) : (
            <div
              ref={mapElRef}
              style={{ width: '100%', height: 350, background: '#e8e8e8' }}
              aria-label="좌담회 장소 지도"
            />
          )}
        </div>
        <p className="address">
          <strong>{loc.addressLine}</strong>
        </p>
        <ul className="info-list">
          <li>
            ※ 대중교통을 이용해 주십시오 <em className="color-danger">*주차불가</em>
          </li>
          <li>※ 아래 [오시는 길 안내]를 참고해 주십시오.</li>
        </ul>
        <div className="c-popup__footer">
          <button type="button" className="btn btn--md btn--light" onClick={onClose}>
            닫기
          </button>
          <Link
            to={paths.footer.location}
            target="_blank"
            rel="noreferrer"
            className="btn btn--md btn--primary"
            title="새창열림"
          >
            오시는 길 안내
          </Link>
        </div>
      </>
    </Popup>
  );
}
